import React, { useState } from 'react';
import {
	ArrowRightIcon,
	CheckIcon,
	ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import client from '../api/client';
import { useAlert } from '../context/AlertContext';

function Eyebrow({ children, dark = false }) {
	return (
		<p
			className={`text-xs font-semibold uppercase tracking-[0.18em] ${
				dark ? 'text-primary-400' : 'text-primary-600'
			}`}
		>
			<span className='inline-block w-8 h-px align-middle mr-3 bg-current opacity-60' />
			{children}
		</p>
	);
}

// Common questions, lifted from Gabe's copy. Clicking one pre-fills the box so
// people who aren't sure how to phrase it can get started in one tap.
const COMMON_QUESTIONS = [
	'Is solar worth it?',
	'Should I add batteries?',
	'How much can I save?',
	'What happens if I move?',
	'Is my roof a good candidate?',
	'Which equipment should I choose?',
];

// "Ask Gabe" — per the feedback, potentially the highest-converting feature.
// It reuses the existing /message endpoint (tagged heardFrom: 'Ask Gabe') so
// questions land in the same inbox/CRM as every other lead — no backend change.
export default function AskGabe() {
	const { setAlert } = useAlert();
	const [form, setForm] = useState({
		name: '',
		email: '',
		question: '',
		website: '', // honeypot — must stay empty
	});
	const [submitting, setSubmitting] = useState(false);
	const [done, setDone] = useState(false);

	const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

	const submit = async (e) => {
		e.preventDefault();
		// Honeypot tripped: pretend success, send nothing.
		if (form.website) return setDone(true);
		if (!form.name || !form.email || !form.question) {
			return setAlert('error', 'Please add your name, email, and a question.');
		}
		setSubmitting(true);
		try {
			await client.post('/message', {
				name: form.name,
				email: form.email,
				heardFrom: 'Ask Gabe',
				message: `Ask Gabe question:\n${form.question}`,
			});
			setDone(true);
		} catch (err) {
			setAlert('error', err.message || 'Something went wrong.');
		} finally {
			setSubmitting(false);
		}
	};

	const fieldCls =
		'block w-full rounded-2xl border-0 px-4 py-3 text-base text-ink-900 ring-1 ring-inset ring-ink-300 placeholder:text-ink-300 focus:ring-2 focus:ring-inset focus:ring-primary-500';
	const labelCls = 'mb-1.5 block text-sm font-medium text-ink-600';

	return (
		<div className='bg-surface text-ink-900 antialiased'>
			{/* Hero */}
			<section className='relative isolate overflow-hidden bg-ink-900'>
				<div
					className='absolute inset-0 -z-10 opacity-25'
					style={{
						backgroundImage:
							'radial-gradient(circle at 20% 20%, #3CB371 0%, transparent 45%), radial-gradient(circle at 85% 90%, #2A7E4F 0%, transparent 55%)',
					}}
				/>
				<div className='mx-auto max-w-7xl px-6 py-20 sm:py-28 lg:px-8'>
					<div className='max-w-2xl'>
						<Eyebrow dark>Ask Gabe</Eyebrow>
						<h1 className='font-display mt-6 text-4xl font-medium tracking-tight text-white sm:text-5xl lg:text-6xl leading-[1.08]'>
							Ask me a{' '}
							<span className='italic text-primary-400'>solar</span> question.
						</h1>
						<p className='mt-8 text-lg leading-8 text-white/75'>
							Whether you&apos;re considering solar, comparing quotes, looking at
							battery backup, or just trying to understand your options — submit
							your question and I&apos;ll personally review it. No pressure, no
							obligation.
						</p>
					</div>
				</div>
			</section>

			<section className='py-20 sm:py-28'>
				<div className='mx-auto max-w-3xl px-6 lg:px-8'>
					{done ? (
						<div className='sg-card bg-white p-10 text-center sm:p-14'>
							<div className='mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100'>
								<CheckIcon className='h-9 w-9 text-primary-600' />
							</div>
							<h2 className='mt-6 font-display text-3xl font-medium text-ink-900'>
								Got it{form.name ? `, ${form.name.split(' ')[0]}` : ''}!
							</h2>
							<p className='mx-auto mt-3 max-w-md text-base leading-7 text-ink-500'>
								I personally review every question and I&apos;ll get back to you
								at {form.email || 'your email'} soon.
							</p>
						</div>
					) : (
						<div className='sg-card bg-white p-8 sm:p-12'>
							<div className='flex items-center gap-3'>
								<div className='sg-badge'>
									<ChatBubbleLeftRightIcon className='h-5 w-5' />
								</div>
								<h2 className='font-display text-2xl font-medium text-ink-900'>
									Your question
								</h2>
							</div>

							{/* Common questions — tap to pre-fill */}
							<p className='mt-8 text-sm font-medium text-ink-600'>
								Not sure where to start? Try one of these:
							</p>
							<div className='mt-3 flex flex-wrap gap-2'>
								{COMMON_QUESTIONS.map((q) => (
									<button
										key={q}
										type='button'
										onClick={() => setForm((f) => ({ ...f, question: q }))}
										className='rounded-full bg-surface px-4 py-2 text-sm font-medium text-ink-700 ring-1 ring-ink-200 transition-colors hover:bg-primary-50 hover:text-primary-700 hover:ring-primary-300'
									>
										{q}
									</button>
								))}
							</div>

							<form
								onSubmit={submit}
								className='mt-8 space-y-5'
							>
								<div>
									<label className={labelCls}>
										Your question <span className='text-rose-500'>*</span>
									</label>
									<textarea
										rows={4}
										value={form.question}
										onChange={set('question')}
										className={fieldCls}
										placeholder='What would you like to know?'
									/>
								</div>
								<div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
									<div>
										<label className={labelCls}>
											Name <span className='text-rose-500'>*</span>
										</label>
										<input
											value={form.name}
											onChange={set('name')}
											className={fieldCls}
											autoComplete='name'
										/>
									</div>
									<div>
										<label className={labelCls}>
											Email <span className='text-rose-500'>*</span>
										</label>
										<input
											type='email'
											value={form.email}
											onChange={set('email')}
											className={fieldCls}
											autoComplete='email'
										/>
									</div>
								</div>

								{/* Honeypot — visually hidden; bots fill it, humans don't. */}
								<div
									aria-hidden='true'
									style={{
										position: 'absolute',
										left: '-10000px',
										width: '1px',
										height: '1px',
										overflow: 'hidden',
									}}
								>
									<label htmlFor='website'>Website</label>
									<input
										id='website'
										name='website'
										type='text'
										tabIndex={-1}
										autoComplete='off'
										value={form.website}
										onChange={set('website')}
									/>
								</div>

								<div className='pt-2'>
									<button
										type='submit'
										disabled={submitting}
										className='inline-flex items-center gap-2 rounded-full bg-primary-500 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-primary-500/25 hover:bg-primary-600 disabled:opacity-60 transition-all'
									>
										{submitting ? 'Sending…' : 'Submit my question'}
										{!submitting && <ArrowRightIcon className='h-5 w-5' />}
									</button>
								</div>
								<p className='text-xs leading-5 text-ink-400'>
									My goal is to help Maryland homeowners make informed decisions.
									We&apos;ll never sell or share your information.
								</p>
							</form>
						</div>
					)}
				</div>
			</section>
		</div>
	);
}
